/**
 * Message-code text -- the `message` and `title` the API sends with an error, or the copy the client
 * owns for the codes it raises itself -- is plain text. It carries two kinds of structure, neither of
 * them markup:
 *
 *  - a line break, written `\n`;
 *  - a support number, written `{support:<key>}` and resolved against the `config.support` keys.
 *
 * Splitting the text into segments lets a template render each piece through interpolation, so the
 * string never reaches `innerHTML` and any markup in it stays literal.
 */

/** Support numbers keyed like `config.support`. */
export type SupportNumbers = Readonly<Record<string, string | undefined>>;

export type MessageSegment =
  | { readonly kind: 'text', readonly value: string }
  | { readonly kind: 'break' }
  | { readonly kind: 'phone', readonly number: string };

const Token = /\{support:([A-Za-z0-9]+)\}|\r?\n/g;

export function parseMessageSegments(text: string | null | undefined, support: SupportNumbers): MessageSegment[] {
  if (!text) {
    return [];
  }

  const segments: MessageSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(Token)) {
    const index = match.index ?? 0;
    if (index > last) {
      segments.push({kind: 'text', value: text.slice(last, index)});
    }

    const key = match[1];
    if (key === undefined) {
      segments.push({kind: 'break'});
    } else {
      // `config.support` already falls back to `default` for a missing key; a plain map gets the same here
      segments.push({kind: 'phone', number: support[key] || support['default'] || ''});
    }
    last = index + match[0].length;
  }

  if (last < text.length) {
    segments.push({kind: 'text', value: text.slice(last)});
  }
  return segments;
}

/** The text with placeholders resolved, for places that only take a string (a card or callout title). */
export function renderMessageText(text: string | null | undefined, support: SupportNumbers): string {
  return parseMessageSegments(text, support).map(segment => {
    switch (segment.kind) {
      case 'text':
        return segment.value;
      case 'break':
        return '\n';
      case 'phone':
        return segment.number;
    }
  }).join('');
}
